import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAthleteTargets, type AthleteTargets } from "@jackedlogs/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { ProgramsCache } from "../Programs/usePrograms";

// ME-3e — the athlete's OWN weekly targets (GET /api/me/targets), for the
// web-rnw console's ProgramComposer mount, which had no fetch for this at all.
//
// ⚠️ NOT shared yet, and the honest version of that: mobile reads the same
// document through apps/mobile/src/progress/useAthleteTargets.ts (a thin
// wrapper over useAsyncResource) and its 4 callsites were deliberately NOT
// repointed here. One of them feeds the in-workout target strip on the live
// logging path, and the two hooks do not have the same contract: that one
// cancels in flight via AbortController and exposes `error: ApiError`, this
// one does neither. Repointing is the right end state and it is tracked
// (backlog CN-athlete-targets-dup), but it is a hot-path refactor that wants
// a device check, not an unattended edit.
// TODO(cleanup): repoint mobile onto this hook and delete the mobile copy.
//
// Mirrors usePrograms/useCatalogItems: apiFetch
// + an optional structurally-typed cache adapter (mobile hands an AsyncStorage
// adapter; web-rnw a localStorage one), no platform coupling in the hook
// itself. Reuses the shared `fetchAthleteTargets` reader (already the single
// fetch+parse used by the coach-side hooks) instead of a second hand-rolled
// GET+parse.
//
// The server's GET always returns a body: it synthesizes an empty doc
// (items: []) when the athlete has never set targets. So `value` is null
// only while loading — never as the "no targets" state. A LOADED doc with
// items.length === 0 IS "no goals yet".

export type AthleteTargetsResourceStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "refreshing"
  | "error";

export type UseAthleteTargetsOptions = {
  apiFetch: ProgramApiFetch;
  cache?: ProgramsCache<AthleteTargets>;
};

export type AthleteTargetsState = {
  value: AthleteTargets | null;
  status: AthleteTargetsResourceStatus;
  refresh: () => Promise<void>;
};

export function useAthleteTargets(
  opts: UseAthleteTargetsOptions,
): AthleteTargetsState {
  const [value, setValue] = useState<AthleteTargets | null>(null);
  const [status, setStatus] = useState<AthleteTargetsResourceStatus>("idle");

  // Refs so a host passing fresh literals per render can't re-trigger the
  // mount effect (the unstable-object-dep lesson — see usePrograms).
  const apiRef = useRef(opts.apiFetch);
  const cacheRef = useRef(opts.cache);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    cacheRef.current = opts.cache;
  });

  const refresh = useCallback(async (): Promise<void> => {
    setStatus((prev) => (prev === "loaded" ? "refreshing" : "loading"));
    const fresh = await fetchAthleteTargets(apiRef.current);
    if (fresh === null) {
      setStatus("error");
      return;
    }
    setValue(fresh);
    setStatus("loaded");
    void cacheRef.current?.write(fresh);
  }, []);

  // Mount: hydrate from the platform cache (if any), then refresh.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cached = await cacheRef.current?.read();
      if (!cancelled && cached != null) {
        setValue(cached);
        setStatus("loaded");
      }
      void refresh();
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  return { value, status, refresh };
}
